import React, { useState } from "react";
import { Card, Badge, Button, Stack, ListGroup, Collapse } from "react-bootstrap";
import { Droppable, Draggable } from "react-beautiful-dnd";

/* =======================
   TYPES
======================= */
interface PlannedItem {
  planningId: number;
  productName: string;
  plannedQuantity: number;
  plannedPalletIndex?: number;
}

interface Order {
  orderId?: number;
  ordersId?: number;
  buyerName: string;
  orderNumber?: string;
  targetWarehouse?: string;
  items?: PlannedItem[];
}

interface Shipment {
  id: number;
  shipmentCode: string;
  vehiclePlate?: string;
  statusLabel: string;
  orders: Order[];
}

interface ShipmentBoardProps {
  shipments: Shipment[];
  onDetailClick: (id: number) => void;
  onCompleteClick: (id: number) => void;
  onShowMap: (shipment: any) => void;
}

/* =======================
   SUB-COMPONENT: Sipariş Kartı (Aracın İçindeki)
======================= */
const ShipmentOrderCard = ({ order, index }: { order: Order; index: number }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const safeOrderId = order.orderId || order.ordersId;
  const displayOrderNumber = order.orderNumber || safeOrderId;
  const itemCount = order.items ? order.items.length : 0;

  return (
    <Draggable draggableId={`order-${safeOrderId}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          // Sipariş kartları için çok ince, zarif bir gri kenarlık
          className={`mb-2 rounded bg-white ${
            snapshot.isDragging ? "shadow-lg border-primary" : "shadow-sm"
          }`}
          style={{
            ...provided.draggableProps.style,
            border: "1px solid #e3e6f0"
          }}
        >
          <div className="p-3 d-flex justify-content-between align-items-start cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
            <div>
              <div className="fw-bold text-primary small mb-1">#{displayOrderNumber}</div>
              <div className="text-dark fw-bold small mb-1">{order.buyerName}</div>
              <div className="text-muted small">
                <span style={{ color: "#dc3545", marginRight: "4px" }}>📍</span>
                {order.targetWarehouse || "Depo Yok"}
              </div>
            </div>
            <div className="text-end">
              <Badge bg="light" text="dark" className="border mb-1" style={{borderColor: "#e3e6f0"}}>{itemCount} Kalem</Badge>
              <div className="text-muted small" style={{ fontSize: "0.7rem" }}>{isExpanded ? "▲ Gizle" : "▼ Detay"}</div>
            </div>
          </div>

          {isExpanded && order.items && (
            <div className="border-top bg-light p-2" style={{borderColor: "#e3e6f0"}}>
              <ListGroup variant="flush" className="small">
                {order.items.map((item) => (
                  <ListGroup.Item key={item.planningId} className="bg-transparent py-1 px-0 d-flex justify-content-between align-items-center">
                    <span className="text-truncate" style={{ maxWidth: "80%" }}>{item.productName}</span>
                    <span className="fw-bold badge bg-secondary text-white">{item.plannedQuantity}</span>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

/* =======================
   SUB-COMPONENT: Araç Kartı (KATLANABİLİR + EMOJİLİ)
======================= */
const ShipmentVehicleCard = ({
  shipment,
  onShowMap,
  onDetailClick,
  onCompleteClick
}: {
  shipment: Shipment,
  onShowMap: any,
  onDetailClick: any,
  onCompleteClick: any
}) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    // Araç Kartı Kenarlığı: Orta ton gri (#d1d3e2)
    <Card className="mb-3 shadow-sm" style={{ border: "1px solid #d1d3e2", borderRadius: "8px" }}>

      {/* HEADER */}
      <Card.Header className="bg-white py-2" style={{ borderBottom: "1px solid #eaecf4", borderRadius: "8px 8px 0 0" }}>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">

          {/* SOL: Ok ve Plaka */}
          <div className="d-flex align-items-center gap-2 cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
            <div className="text-secondary" style={{ fontSize: "1.1rem", transition: "transform 0.2s", transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)" }}>
               <i className="bi bi-chevron-down"></i>
            </div>

            <div>
              <div className="d-flex align-items-center gap-2">
                 <span className="fw-bold text-dark fs-5">{shipment.vehiclePlate || "PLAKA YOK"}</span>
                 <Badge bg="info" className="fw-normal" style={{fontSize: "0.75rem"}}>{shipment.statusLabel}</Badge>
              </div>
              <div className="small text-muted" style={{fontSize: "0.8rem"}}>Kod: {shipment.shipmentCode}</div>
            </div>
          </div>

          {/* SAĞ: Butonlar (Yazılı + İkonlu) */}
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={() => onShowMap(shipment)}>
               🗺️ Rota
            </Button>

            <Button size="sm" variant="outline-primary" onClick={() => onDetailClick(shipment.id)}>
                   Paletleri Planla
            </Button>

            <Button size="sm" variant="success" onClick={() => onCompleteClick(shipment.id)}>
               ✓ Onayla
            </Button>
          </div>

        </div>
      </Card.Header>

      {/* BODY */}
      <Collapse in={isOpen}>
        <div>
          <Droppable droppableId={`shipment-${shipment.id}`} type="ORDER">
            {(provided, snapshot) => (
              <Card.Body
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`p-2 transition-all ${snapshot.isDraggingOver ? "bg-success-subtle" : ""}`}
                style={{
                    minHeight: "100px",
                    maxHeight: "450px",
                    overflowY: "auto",
                    transition: "background-color 0.2s"
                }}
              >
                {(!shipment.orders || shipment.orders.length === 0) && (
                  <div className="text-center text-muted small py-5 border border-dashed rounded mt-1" style={{borderColor: "#e3e6f0"}}>
                    <i className="bi bi-box-arrow-in-down fs-3 d-block mb-2 opacity-50"></i>
                    Siparişi buraya sürükleyin
                  </div>
                )}

                <div className="mt-1">
                    {shipment.orders?.map((order, index) => (
                    <ShipmentOrderCard key={order.orderId || order.ordersId} order={order} index={index} />
                    ))}
                </div>

                {provided.placeholder}
              </Card.Body>
            )}
          </Droppable>

          {/* Alt Bilgi */}
          {shipment.orders && shipment.orders.length > 0 && (
             <div className="border-top p-2 text-center bg-light small text-muted fw-bold" style={{borderColor: "#eaecf4"}}>
                 Toplam {shipment.orders.length} Sipariş
             </div>
          )}
        </div>
      </Collapse>
    </Card>
  );
};


/* =======================
   MAIN COMPONENT
======================= */
const ShipmentBoard: React.FC<ShipmentBoardProps> = ({ shipments, onDetailClick, onCompleteClick, onShowMap }) => {
  return (
    <div className="col-md-7">
        {/* 🔥 EN DIŞ ÇERÇEVE GÜNCELLEMESİ:
           - style={{ border: "1px solid #c5c9d6" }} ekledim.
           - Bu renk, standart gri'den (secondary) bir tık daha yumuşak ama border-0'dan çok daha belirgindir.
        */}
        <Card className="h-100 shadow-sm" style={{ backgroundColor: "#f8f9fa", border: "1px solid #c5c9d6" }}>

            <Card.Header className="bg-white p-3" style={{ borderBottom: "1px solid #c5c9d6" }}>
                <h6 className="m-0 text-dark fw-bold">
                    <i className="bi bi-truck me-2"></i>
                    Aktif Sevkiyat Planları
                </h6>
            </Card.Header>

            <Card.Body className="p-3" style={{ overflowY: "auto", maxHeight: "80vh" }}>

                {shipments.length === 0 ? (
                    <div className="text-center p-5">
                        <i className="bi bi-truck fs-1 d-block mb-3 text-secondary opacity-50"></i>
                        <h6 className="fw-bold text-muted">Henüz Plan Yok</h6>
                        <p className="small text-muted">Sol taraftan siparişleri sürüklemek için yeni bir sevkiyat oluşturun.</p>
                    </div>
                ) : (
                    shipments.map((shipment) => (
                        <ShipmentVehicleCard
                            key={shipment.id}
                            shipment={shipment}
                            onShowMap={onShowMap}
                            onDetailClick={onDetailClick}
                            onCompleteClick={onCompleteClick}
                        />
                    ))
                )}

            </Card.Body>
        </Card>
    </div>
  );
};

export default ShipmentBoard;